import { UserClaims } from "../types";

/**
 * Auth token store, backed by sessionStorage rather than localStorage.
 *
 * sessionStorage survives a page refresh (required: sessions must persist
 * across reloads) but is scoped to the browser tab -- it's never shared
 * across tabs and is wiped when the tab closes, unlike localStorage which
 * persists indefinitely across browser restarts.
 *
 * This is a documented interim-risk-accepted tradeoff for RCPLATFORM-416
 * (WR-05): sessionStorage is still readable by same-origin JS, so it does
 * not fully close the CWE-922 finding on its own. The restrictive CSP
 * deployed alongside this change (script-src 'self', no unsafe-inline/eval)
 * is the actual mitigation -- it makes the script-injection attack vector
 * that would read this storage much harder to pull off in the first place.
 */
const TOKEN_KEY = "bri_session_token";
const REFRESH_TOKEN_KEY = "bri_session_refresh_token";
const CLAIMS_CACHE_KEY = "bri_session_verify_claims_cache";

const readItem = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeItem = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
};

const removeItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
};

export const tokenStore = {
  getToken(): string | null {
    return readItem(TOKEN_KEY);
  },

  setToken(token: string): void {
    writeItem(TOKEN_KEY, token);
  },

  getRefreshToken(): string | null {
    return readItem(REFRESH_TOKEN_KEY);
  },

  setRefreshToken(token: string): void {
    writeItem(REFRESH_TOKEN_KEY, token);
  },

  getCachedClaims(token: string): UserClaims | null {
    const raw = readItem(CLAIMS_CACHE_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as { token?: string; claims?: UserClaims };
      if (!parsed.token || parsed.token !== token || !parsed.claims) {
        return null;
      }
      return parsed.claims;
    } catch {
      return null;
    }
  },

  setCachedClaims(token: string, claims: UserClaims): void {
    writeItem(CLAIMS_CACHE_KEY, JSON.stringify({ token, claims }));
  },

  clear(): void {
    removeItem(TOKEN_KEY);
    removeItem(REFRESH_TOKEN_KEY);
    removeItem(CLAIMS_CACHE_KEY);
  },
};
